"use client"

import {
  Building2,
  ChefHat,
  Globe,
  Image,
  Info,
  MapPin,
  MessagesSquare,
  Percent,
  Phone,
  Star,
  Tags,
  Utensils
} from "lucide-react"
import * as React from "react"

import { NavMain } from "@/components/nav-main"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { RestaurantFeedback } from "../restaurants/restaurant-feedback"
import { Button } from "../ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"

const restaurantNavItems = [
  {
    title: "Overview",
    url: "#overview",
    icon: Info,
    items: [
      {
        title: "Basic Info",
        url: "#basic-info",
      },
      {
        title: "Hours",
        url: "#hours",
      },
      {
        title: "Rating",
        url: "#rating",
      },
    ],
  },
  {
    title: "Photos",
    url: "#photos",
    icon: Image,
    items: [
      {
        title: "Gallery",
        url: "#gallery",
      },
    ],
  },
  {
    title: "Contact",
    url: "#contact",
    icon: Phone,
    items: [
      {
        title: "Location",
        url: "#location",
        icon: MapPin,
      },
      {
        title: "Phone",
        url: "#phone",
      },
      {
        title: "Website",
        url: "#website",
        icon: Globe,
      },
    ],
  },
  {
    title: "Features",
    url: "#features",
    icon: Tags,
    items: [
      {
        title: "Service Options",
        url: "#service-options",
        icon: Utensils,
      },
      {
        title: "Meals Served",
        url: "#meals-served",
        icon: ChefHat,
      },
      {
        title: "Amenities",
        url: "#amenities",
        icon: Building2,
      },
    ],
  },
  {
    title: "Reviews",
    url: "#reviews",
    icon: Star,
    items: [
      {
        title: "All Reviews",
        url: "#reviews",
        icon: Star,
      },
      {
        title: "Write a Review",
        url: "#write-review",
      },
    ],
  },
  {
    title: "Menu",
    url: "#menu",
    icon: Utensils,
    disabled: true,
    items: [
      {
        title: "Full Menu",
        url: "#full-menu",
        disabled: true,
      },
    ],
  },
  {
    title: "Offers",
    url: "#offers",
    icon: Percent,
    disabled: true,
    items: [
      {
        title: "Current Offers",
        url: "#current-offers",
        disabled: true,
      },
    ],
  },
]

export function RestaurantSidebarLeft({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const [showFeedback, setShowFeedback] = React.useState(false);
  const { state } = useSidebar();

  const handleFeedbackClick = () => {
    setShowFeedback(true);
  };
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    e.preventDefault();
    const element = document.querySelector(url);
    if (element) {
      const yOffset = -160;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <Sidebar 
      className="flex flex-col h-screen" 
      collapsible="icon" 
      {...props}
    >
      <SidebarContent className="flex-1 mt-12">
        <NavMain
          items={restaurantNavItems}
          onNavClick={handleNavClick}
        />
      </SidebarContent>
      <SidebarFooter>
        {state === 'expanded' ? (
          <div className="w-full">
            <Card className="bg-card rounded-lg">
              <CardHeader className="p-3 flex flex-col gap-1">
                <CardTitle className="text-sm font-normal">Are you a local guide?</CardTitle>
                <CardDescription className="text-sm">
                  We&apos;d love to hear from you.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-3 pt-4">
                <Button
                  size="sm"
                  className="w-full"
                  onClick={handleFeedbackClick}
                  variant="default"
                >
                  Get in touch!
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          <TooltipProvider>
            <Tooltip delayDuration={50}>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={handleFeedbackClick}
                >
                  <MessagesSquare/>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                Are you a local guide?
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {showFeedback && (
          <RestaurantFeedback
            isOpen={showFeedback}
            onClose={() => setShowFeedback(false)}
          />
        )}
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
